import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { createContext, ReactNode, useCallback, useContext, useRef } from "react";
import { getConfig } from "../config";

// API Types from https://docs.klipy.com/migrate-from-tenor/response-objects/response-object
interface MediaFormat {
	url: string;
	duration: number;
	preview?: string;
	dims: number[];
	size: number;
}

interface MediaFormats {
	gif?: MediaFormat;
	mediumgif?: MediaFormat;
	tinygif?: MediaFormat;
	nanogif?: MediaFormat;
	mp4?: MediaFormat;
	loopedmp4?: MediaFormat;
	tinymp4?: MediaFormat;
	nanomp4?: MediaFormat;
	webm?: MediaFormat;
	tinywebm?: MediaFormat;
	nanowebm?: MediaFormat;
	webp_transparent?: MediaFormat;
	gif_transparent?: MediaFormat;
	png_transparent?: MediaFormat;
}

interface KlipyGifResult {
	id: string;
	title: string;
	media_formats: MediaFormats;
	created: number;
	content_description: string;
	itemurl: string;
	url: string;
	tags: string[];
	flags: string[];
	hasaudio: boolean;
}

interface KlipyApiResponse {
	results: KlipyGifResult[];
	next?: string;
}

// Search-specific response that includes pagination
interface KlipySearchResponse extends KlipyApiResponse {
	next?: string; // Pagination token for next page
}

// Our simplified GIF data interface
interface GifData {
	id: string;
	url: string;
	preview: string;
	title: string;
}

// Search parameters interface
interface SearchParams {
	query: string;
	limit?: number;
	pos?: string; // Pagination position from previous response
	contentfilter?: "off" | "low" | "medium" | "high";
	media_filter?: string;
}

// Klipy API resource types
type KlipyResource =
	| "Posts"
	| "SearchPosts"
	| "Autocomplete"
	| "Categories"
	| "FeaturedPosts"
	| "RegisterShare"
	| "SearchSuggestions"
	| "TrendingTerms";

// Pending request for a single GIF, broken up this way to allow individual resolution/rejection
interface PendingGifRequest {
	id: string;
	resolve: (gif: GifData | null) => void;
	reject: (error: Error) => void;
}

// Pending search request
interface PendingSearchRequest {
	searchParams: SearchParams;
	resolve: (response: KlipySearchResponse) => void;
	reject: (error: Error) => void;
}

// Rate limiter for each resource, this will ensure we don't exceed the allowed request rate of 1/second
// This is scoped to each resource type as defined in the Klipy API quota in the cloud console.
class ResourceRateLimiter {
	private lastRequestTime = 0;
	private readonly rateLimitMs: number;

	constructor(requestsPerSecond: number = 1) {
		this.rateLimitMs = 1000 / requestsPerSecond;
	}

	async waitForTurn(): Promise<void> {
		const now = Date.now();
		const timeSinceLastRequest = now - this.lastRequestTime;

		if (timeSinceLastRequest < this.rateLimitMs) {
			const waitTime = this.rateLimitMs - timeSinceLastRequest;
			await new Promise((resolve) => setTimeout(resolve, waitTime));
		}

		this.lastRequestTime = Date.now();
	}
}

// Context interface
interface GifContextValue {
	requestGif: (id: string) => Promise<GifData | null>;
	searchGifs: (searchParams: SearchParams) => Promise<KlipySearchResponse>;
	convertKlipyResultToGifData: (gif: KlipyGifResult) => GifData;
}

const GifContext = createContext<GifContextValue | null>(null);

// Provider component
interface GifProviderProps {
	children: ReactNode;
}

export const GifProvider: React.FC<GifProviderProps> = ({ children }) => {
	const queryClient = useQueryClient();

	// Rate limiters for different resources
	const rateLimiters = useRef<Record<KlipyResource, ResourceRateLimiter>>({
		Posts: new ResourceRateLimiter(1),
		SearchPosts: new ResourceRateLimiter(1),
		Autocomplete: new ResourceRateLimiter(1),
		Categories: new ResourceRateLimiter(1),
		FeaturedPosts: new ResourceRateLimiter(1),
		RegisterShare: new ResourceRateLimiter(1),
		SearchSuggestions: new ResourceRateLimiter(1),
		TrendingTerms: new ResourceRateLimiter(1),
	});

	// Pending GIF requests that will be batched
	const pendingPostRequests = useRef<Map<string, PendingGifRequest>>(new Map());

	// Pending search requests (we'll batch these too, but they're more immediate)
	const pendingSearchRequests = useRef<PendingSearchRequest[]>([]);

	// Debounce timers for batching requests
	const postBatchTimer = useRef<NodeJS.Timeout | null>(null);
	const searchBatchTimer = useRef<NodeJS.Timeout | null>(null);

	const getKlipyApiKey = useCallback(() => {
		const apiKey = getConfig().klipyKey;
		if (!apiKey) {
			throw new Error("Klipy API key is not configured");
		}
		return apiKey;
	}, []);

	/**
	 * Helper function to make requests to the Klipy API
	 * @param endpoint - The Klipy API endpoint to call
	 * @param params - Query parameters for the request, this should not include the API key
	 * @returns Parsed JSON response from the Klipy API
	 */
	const makeKlipyRequest = useCallback(
		async (
			endpoint: string,
			params: Record<string, string>,
		): Promise<KlipyApiResponse | KlipySearchResponse> => {
			const apiKey = getKlipyApiKey();
			const url = new URL(`https://api.klipy.com/v2/${endpoint}`);

			// Add API key and other parameters
			url.searchParams.set("key", apiKey);
			Object.entries(params).forEach(([key, value]) => {
				url.searchParams.set(key, value);
			});

			const response = await fetch(url.toString(), {
				method: "GET",
				headers: {
					Accept: "application/json",
				},
			});

			if (!response.ok) {
				const errorText = await response.text();
				throw new Error(`Klipy API error ${response.status}: ${errorText}`);
			}

			return response.json();
		},
		[getKlipyApiKey],
	);

	// Helper function to convert Klipy API response to our GifData format
	const convertKlipyResultToGifData = useCallback(
		(gif: KlipyGifResult): GifData => ({
			id: gif.id,
			url: gif.media_formats?.gif?.url || gif.url,
			preview: gif.media_formats?.tinygif?.url || gif.url,
			title: gif.title || gif.content_description,
		}),
		[],
	);

	const executeBatchPostRequests = useCallback(async () => {
		// Just to note: The reason we can use pendingPostRequests here without it being in the dependency array
		// is because it's a ref and we are only reading its current value. React refs are mutable containers whose .current property
		// can be changed without causing a re-render, so they don't need to be included in dependency arrays.
		const requests = Array.from(pendingPostRequests.current.values());
		const ids = Array.from(pendingPostRequests.current.keys());

		// Clear pending requests
		pendingPostRequests.current.clear();

		if (requests.length === 0) return;

		try {
			// Wait for rate limit
			await rateLimiters.current.Posts.waitForTurn();

			// Make the batch request to Klipy's posts endpoint
			const response = (await makeKlipyRequest("posts", {
				ids: ids.join(","),
				media_filter: "gif,tinygif", // Only request the formats we need
			})) as KlipyApiResponse;

			// Process results and add to query cache, we are setting them individually
			// to avoid invalidating existing cache entries unnecessarily
			const resultsMap = new Map<string, GifData>();

			if (response?.results) {
				response.results.forEach((gif: KlipyGifResult) => {
					const gifData = convertKlipyResultToGifData(gif);
					resultsMap.set(gif.id, gifData);
					queryClient.setQueryData(gifQueries.single(gif.id), gifData);
				});
			}

			// Resolve all pending requests
			requests.forEach((request) => {
				// Request.id is a number, but our keys are strings. We need to convert to string to match
				const gifData = resultsMap.get(`${request.id}`);
				request.resolve(gifData || null);
			});
		} catch (error) {
			// Reject all pending requests with the error
			requests.forEach((request) => {
				request.reject(error as Error);
			});
		}
	}, [makeKlipyRequest, queryClient, convertKlipyResultToGifData]);

	// Execute batched search requests
	const executeBatchSearchRequests = useCallback(async () => {
		// Only process the most recent search request to avoid unnecessary API calls
		// when users are typing and generating multiple search requests
		if (pendingSearchRequests.current.length === 0) return;

		const request = pendingSearchRequests.current.pop(); // Get the most recent request
		const rejectedRequests = pendingSearchRequests.current.splice(0); // Get all other requests to reject

		if (!request) return;

		// Reject all older requests since they're no longer needed
		rejectedRequests.forEach((oldRequest) => {
			oldRequest.reject(new Error("Search superseded by newer search"));
		});

		try {
			// Wait for rate limit
			await rateLimiters.current.SearchPosts.waitForTurn();

			// Convert SearchParams to query parameters
			const params: Record<string, string> = {
				q: request.searchParams.query,
				limit: String(request.searchParams.limit || 20),
				media_filter: request.searchParams.media_filter || "gif,tinygif",
				contentfilter: request.searchParams.contentfilter || "low",
			};

			// Add pagination if provided
			if (request.searchParams.pos) {
				params.pos = request.searchParams.pos;
			}

			const response = (await makeKlipyRequest("search", params)) as KlipySearchResponse;

			// Cache individual GIFs from search results
			if (response?.results) {
				response.results.forEach((gif: KlipyGifResult) => {
					const gifData = convertKlipyResultToGifData(gif);
					queryClient.setQueryData(gifQueries.single(gif.id), gifData);
				});
			}

			request.resolve(response);
		} catch (error) {
			request.reject(error as Error);
		}
	}, [makeKlipyRequest, queryClient, convertKlipyResultToGifData]);

	// Schedule batch execution with debouncing for posts
	const schedulePostBatchExecution = useCallback(() => {
		if (postBatchTimer.current) {
			clearTimeout(postBatchTimer.current);
		}

		postBatchTimer.current = setTimeout(() => {
			executeBatchPostRequests();
		}, 1000); // 1 second debounce
	}, [executeBatchPostRequests]);

	// Schedule search execution (shorter debounce for better UX)
	const scheduleSearchBatchExecution = useCallback(() => {
		if (searchBatchTimer.current) {
			clearTimeout(searchBatchTimer.current);
		}

		searchBatchTimer.current = setTimeout(() => {
			executeBatchSearchRequests();
		}, 300); // 300ms debounce for searches
	}, [executeBatchSearchRequests]);

	// Request a single GIF (will be batched under the hood)
	const requestGif = useCallback(
		async (id: string): Promise<GifData | null> => {
			if (!id) return null;

			// Check if already cached
			const cached = queryClient.getQueryData<GifData>(gifQueries.single(id));
			if (cached) {
				return cached;
			}

			// Check if request is already pending, we don't want to duplicate requests for the same GIF
			const existingRequest = pendingPostRequests.current.get(id);
			if (existingRequest) {
				return new Promise((resolve, reject) => {
					// Replace the existing resolve/reject to handle multiple requests for the same GIF
					const originalResolve = existingRequest.resolve;
					const originalReject = existingRequest.reject;

					existingRequest.resolve = (gif) => {
						originalResolve(gif);
						resolve(gif);
					};

					existingRequest.reject = (error) => {
						originalReject(error);
						reject(error);
					};
				});
			}

			// Create new pending request
			return new Promise((resolve, reject) => {
				pendingPostRequests.current.set(id, {
					id,
					resolve,
					reject,
				});

				// Schedule batch execution
				schedulePostBatchExecution();
			});
		},
		[queryClient, schedulePostBatchExecution],
	);

	// Search for GIFs (rate limited but not batched like individual requests)
	const searchGifs = useCallback(
		async (searchParams: SearchParams): Promise<KlipySearchResponse> => {
			return new Promise((resolve, reject) => {
				pendingSearchRequests.current.push({
					searchParams,
					resolve,
					reject,
				});

				// Schedule search execution
				scheduleSearchBatchExecution();
			});
		},
		[scheduleSearchBatchExecution],
	);

	const contextValue: GifContextValue = {
		requestGif,
		searchGifs,
		convertKlipyResultToGifData,
	};

	return <GifContext.Provider value={contextValue}>{children}</GifContext.Provider>;
};

// Custom hook to use the GifContext, not exported as we only should be accessing this context via other hooks
const useGifContext = (): GifContextValue => {
	const context = useContext(GifContext);
	if (!context) {
		throw new Error(
			"useGifContext must be used within a GifProvider, please wrap your component tree with GifProvider.",
		);
	}
	return context;
};

const gifQueries = {
	all: ["gifs"] as const, // This is only used for invalidation
	single: (id: string) => [...gifQueries.all, "single", id] as const,
	search: (query: string) => [...gifQueries.all, "search", query] as const,
};

/**
 * Custom hook to fetch a single GIF using the context.
 * This is the main hook to use in components that need GIF data, the options parameter
 * allows for configuring caching behavior and enabling/disabling the query on a per-case basis.
 * * @param id - The ID of the GIF to fetch
 * * @param options - Optional settings for the query
 * - staleTime: How long the data is considered fresh (default 5 minutes)
 * - gcTime: How long unused data is kept in cache (default 10 minutes)
 * - enabled: Whether the query should be enabled (default true if id is provided)
 * @return React Query result containing the GIF data or null if not found
 * */
export const useGif = (
	id: string,
	options?: {
		staleTime?: number;
		gcTime?: number;
		enabled?: boolean;
	},
) => {
	const { requestGif } = useGifContext();

	return useQuery({
		queryKey: gifQueries.single(id),
		queryFn: () => requestGif(id),
		staleTime: options?.staleTime ?? 5 * 60 * 1000, // 5 minutes default
		gcTime: options?.gcTime ?? 10 * 60 * 1000, // 10 minutes, higher than our default to avoid extra refetch requests
		enabled: options?.enabled ?? Boolean(id),
		meta: { suppressErrorModal: true },
		retry: (failureCount, error: any) => {
			if (error?.message?.includes("429") && failureCount < 3) {
				return true;
			}
			return false;
		},
		retryDelay: (attemptIndex) => Math.pow(2, attemptIndex) * 1000,
	});
};

/**
 * Custom hook to search for GIFs with infinite scroll support
 * @param query - Search query string
 * @returns React Query infinite query result with pages of GIF results
 */
export const useGifSearch = (query: string) => {
	const { searchGifs, convertKlipyResultToGifData } = useGifContext();

	return useInfiniteQuery({
		queryKey: gifQueries.search(query),
		queryFn: async ({ pageParam }: { pageParam?: string }) => {
			if (!query || query === "") {
				return { gifs: [], nextCursor: undefined };
			}

			const searchParams: SearchParams = {
				query,
				limit: 20,
				contentfilter: "medium", // PG rating content filter
				media_filter: "gif,tinygif",
				...(pageParam && { pos: pageParam }),
			};

			const response = await searchGifs(searchParams);

			// Convert results to our GifData format
			const gifs: GifData[] = response.results?.map(convertKlipyResultToGifData) || [];

			return {
				gifs,
				nextCursor: response.next,
			};
		},
		getNextPageParam: (lastPage) => lastPage.nextCursor,
		initialPageParam: undefined,
		staleTime: 2 * 60 * 1000, // 2 minutes for search results
		gcTime: 5 * 60 * 1000, // 5 minutes for search results
		retry: (failureCount, error: any) => {
			if (error?.message?.includes("429") && failureCount < 3) {
				return true;
			}
			return false;
		},
		retryDelay: (attemptIndex) => Math.pow(2, attemptIndex) * 1000,
		enabled: Boolean(getConfig().klipyKey), // Only enable if we have an API key, this will prevent maximum stack errors
		meta: { suppressErrorModal: true },
	});
};

// Utility hook to prefetch a single GIF using the context
// Prefetch can be used to add GIFs to the cache ahead of time, for example when hovering over a GIF Preview.
export const useGifPrefetch = () => {
	const queryClient = useQueryClient();
	const { requestGif } = useGifContext();

	const prefetchGif = useCallback(
		async (id: string) => {
			if (!id) return;

			await queryClient.prefetchQuery({
				queryKey: gifQueries.single(id),
				queryFn: () => requestGif(id),
				staleTime: 5 * 60 * 1000,
				gcTime: 10 * 60 * 1000,
				meta: { suppressErrorModal: true },
			});
		},
		[queryClient, requestGif],
	);

	return { prefetchGif };
};

// Utility hook to invalidate cached GIF data
export const useGifCache = () => {
	const queryClient = useQueryClient();

	const invalidateGifs = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: gifQueries.all });
	}, [queryClient]);

	const invalidateGif = useCallback(
		(id: string) => {
			queryClient.invalidateQueries({ queryKey: gifQueries.single(id) });
		},
		[queryClient],
	);

	const invalidateSearch = useCallback(
		(query?: string) => {
			if (query) {
				queryClient.invalidateQueries({ queryKey: gifQueries.search(query) });
			} else {
				// Invalidate all search queries
				queryClient.invalidateQueries({ queryKey: [...gifQueries.all, "search"] });
			}
		},
		[queryClient],
	);

	return { invalidateGifs, invalidateGif, invalidateSearch };
};
